
import React from "react";
import { Subscription } from "@/types/subscription";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface PaymentSummaryProps {
  subscription: Subscription;
  paymentError: string | null;
  isProcessingPayment: boolean;
  onPayment: () => void;
  onCreateNew: () => void;
  discountAmount?: number;
}

export function PaymentSummary({ 
  subscription, 
  paymentError, 
  isProcessingPayment, 
  onPayment, 
  onCreateNew,
  discountAmount = 0 
}: PaymentSummaryProps) {
  // Pricing constants
  const residentialWeeklyRate = 5;
  const businessWeeklyRate = 20;
  const residentialAnnualPrice = 210;
  const businessAnnualPrice = 840;
  
  const weeklyRate = subscription.isBusiness ? businessWeeklyRate : residentialWeeklyRate;
  const annualPrice = subscription.isBusiness ? businessAnnualPrice : residentialAnnualPrice;
  const annualSavings = Math.round((weeklyRate * 52) - annualPrice);
  
  // Calculate final price with discount
  const originalPrice = subscription.isAnnual ? annualPrice : weeklyRate;
  const finalPrice = Math.max(0, originalPrice - discountAmount);

  return (
    <div className="mt-4 mb-6">
      <Alert variant="default" className="bg-amber-50 border-amber-200">
        <AlertDescription className="text-amber-800">
          Subscription created but not yet active. Please complete payment to activate your bin wash service.
        </AlertDescription>
      </Alert>
      <div className="mt-4 p-4 border rounded-lg bg-gray-50">
        <h3 className="font-medium mb-2">Subscription Summary</h3>
        <p><span className="font-medium">Name:</span> {subscription.customerName}</p>
        <p><span className="font-medium">Address:</span> {subscription.address}</p>
        <p><span className="font-medium">Postcode:</span> {subscription.postcode}</p>
        <p><span className="font-medium">Collection Day:</span> {subscription.binDay}</p>
        <p><span className="font-medium">Type:</span> {subscription.isBusiness ? "Business" : "Residential"}</p>
        <p><span className="font-medium">Status:</span> <span className="text-amber-600">Pending Payment</span></p>
        
        {discountAmount > 0 && (
          <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded">
            <p className="text-sm text-green-800">
              <span className="font-medium">Promo Code Applied:</span> {subscription.promoCode}
            </p>
            <p className="text-sm text-green-800">
              <span className="font-medium">Original Price:</span> £{originalPrice}
            </p>
            <p className="text-sm text-green-800">
              <span className="font-medium">Discount:</span> -£{discountAmount}
            </p>
          </div>
        )}
        
        <p className="font-medium mt-2">
          {subscription.isAnnual 
            ? `${discountAmount > 0 ? 'Final ' : ''}Annual Plan: £${finalPrice} ${!discountAmount ? `(Save £${annualSavings})` : ''}${subscription.isBusiness ? " per bin" : ""}` 
            : `${discountAmount > 0 ? 'Final ' : ''}Weekly Plan: £${finalPrice}/week${subscription.isBusiness ? " per bin" : ""}`}
        </p>
        
        {paymentError && (
          <Alert variant="destructive" className="mt-4 mb-2">
            <AlertDescription>
              {paymentError}
            </AlertDescription>
          </Alert>
        )}
        
        <Button 
          variant="green" 
          className="w-full mt-4" 
          onClick={onPayment}
          disabled={isProcessingPayment}
        >
          {isProcessingPayment ? "Processing..." : "Complete Payment to Activate"}
        </Button>
        <Button 
          variant="outline" 
          className="w-full mt-2" 
          onClick={onCreateNew}
          disabled={isProcessingPayment}
        >
          Create New Subscription
        </Button>
      </div>
    </div>
  );
}
